import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useDebounce } from '@shared/hooks/useDebounce';
import type { DateRange } from '@shared/components/FilterPopup';
import { listCertificates } from '../../api/listCertificatesApiService';
import { listCertificateEquipment } from '../../api/listCertificateEquipmentApiService';
import { listTechnicianApiService } from '@epics/technicianManagement/api/listTechnicianApiService';
import type {
  CertificateEquipmentOption,
  CertificateListItem,
} from '../../types/myCertificateTypes';
import type { TechnicianListItem } from '@epics/technicianManagement/types/technicianListType';

const ALLOWED_LIMITS = [10, 30, 50, 100] as const;
const DEFAULT_LIMIT = 10;
const EQUIPMENT_LIMIT = 50;
const TECHNICIAN_LIMIT = 50;
const MAX_SEARCH_LENGTH = 200;

const emptyDateRange: DateRange = { startDate: '', endDate: '' };

const isAllowedLimit = (v: number): boolean =>
  (ALLOWED_LIMITS as readonly number[]).includes(v);

const hasDateFilter = (range: DateRange): boolean =>
  Boolean((range.startDate ?? '').trim() || (range.endDate ?? '').trim());

const isValidSortOrder = (s: string): s is 'asc' | 'desc' =>
  s === 'asc' || s === 'desc';

const idsFromParam = (raw: string | null): string[] => {
  if (!raw) {
    return [];
  }
  return raw
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
};

type AppliedFilters = {
  certificateId: string;
  equipmentIds: string[];
  technicianIds: string[];
  dateRange: DateRange;
};

export interface UseClientCertificateListReturn {
  items: CertificateListItem[];
  loading: boolean;
  page: number;
  setPage: (p: number) => void;
  limit: number;
  totalCount: number;
  isNoFilterFetch: boolean;
  fetchError: boolean;
  accessDenied: boolean;
  search: string;
  isSearchClearing: boolean;
  setSearch: (v: string) => void;
  equipmentIds: string[];
  setEquipmentIds: (v: string[]) => void;
  technicianIds: string[];
  setTechnicianIds: (v: string[]) => void;
  dateRange: DateRange;
  setDateRange: (r: DateRange) => void;
  appliedFilters: AppliedFilters;
  handleApplyFilters: () => void;
  handleClearFilters: () => void;
  limitError: string;
  handleLimitChange: (limit: number) => void;
  sortColumn: 'generated_at';
  sortOrder: 'asc' | 'desc';
  handleSort: (columnKey: string) => void;
  equipmentOptions: CertificateEquipmentOption[];
  equipmentOptionsLoading: boolean;
  equipmentOptionsLoadingMore: boolean;
  loadMoreEquipment: () => void;
  handleEquipmentSearchChange: (q: string) => void;
  technicianOptions: TechnicianListItem[];
  technicianOptionsLoading: boolean;
  technicianOptionsLoadingMore: boolean;
  loadMoreTechnicians: () => void;
  handleTechnicianSearchChange: (q: string) => void;
}

export function useClientCertificateList(): UseClientCertificateListReturn {
  const [searchParams, setSearchParams] = useSearchParams();
  const isSyncingFromUrl = useRef(false);

  // ── Initialize state from URL ─────────────────────────────────────────────
  const initial = (() => {
    const urlPage = searchParams.get('page');
    const urlLimit = searchParams.get('limit');
    const urlSearch = (searchParams.get('search') ?? '').trimStart();
    const urlSortOrder = searchParams.get('sortOrder');
    const urlEquipmentIds = idsFromParam(searchParams.get('equipment'));
    const urlTechnicianIds = idsFromParam(searchParams.get('technician'));
    const urlStartDate = searchParams.get('startDate') ?? '';
    const urlEndDate = searchParams.get('endDate') ?? '';

    const pageNum = urlPage ? parseInt(urlPage, 10) : 1;
    const limitNum = urlLimit ? parseInt(urlLimit, 10) : DEFAULT_LIMIT;

    return {
      page: !isNaN(pageNum) && pageNum >= 1 ? pageNum : 1,
      limit: isAllowedLimit(limitNum) ? limitNum : DEFAULT_LIMIT,
      search:
        urlSearch.length > MAX_SEARCH_LENGTH
          ? urlSearch.slice(0, MAX_SEARCH_LENGTH)
          : urlSearch,
      sortOrder:
        urlSortOrder && isValidSortOrder(urlSortOrder)
          ? urlSortOrder
          : ('desc' as const),
      equipmentIds: urlEquipmentIds,
      technicianIds: urlTechnicianIds,
      dateRange: { startDate: urlStartDate, endDate: urlEndDate },
    };
  })();

  // ── Certificate list state ────────────────────────────────────────────────
  const [items, setItems] = useState<CertificateListItem[]>([]);
  const [page, setPage] = useState(initial.page);
  const [limit, setLimit] = useState(initial.limit);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(false);
  const [accessDenied, setAccessDenied] = useState(false);
  const [limitError, setLimitError] = useState('');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>(initial.sortOrder);

  const [search, setSearchState] = useState(initial.search);
  const [equipmentIds, setEquipmentIds] = useState<string[]>(
    initial.equipmentIds
  );
  const [technicianIds, setTechnicianIds] = useState<string[]>(
    initial.technicianIds
  );
  const [dateRange, setDateRange] = useState<DateRange>(initial.dateRange);
  const [appliedFilters, setAppliedFilters] = useState<AppliedFilters>({
    certificateId: initial.search,
    equipmentIds: initial.equipmentIds,
    technicianIds: initial.technicianIds,
    dateRange: initial.dateRange,
  });

  const debouncedSearch = useDebounce(search, 500);
  // Initialise to initial.search so the "commit debounced search" effect does
  // not fire on first render and reset page to 1 when URL already has a page.
  const prevDebouncedRef = useRef(initial.search);

  // ── Equipment dropdown state ──────────────────────────────────────────────
  const [equipmentOptions, setEquipmentOptions] = useState<
    CertificateEquipmentOption[]
  >([]);
  const [equipmentSearch, setEquipmentSearch] = useState('');
  const [equipmentPage, setEquipmentPage] = useState(1);
  const [equipmentTotal, setEquipmentTotal] = useState(0);
  const [equipmentOptionsLoading, setEquipmentOptionsLoading] = useState(true);
  const [equipmentOptionsLoadingMore, setEquipmentOptionsLoadingMore] =
    useState(false);

  const debouncedEquipmentSearch = useDebounce(equipmentSearch, 500);

  // ── Technician dropdown state ─────────────────────────────────────────────
  const [technicianOptions, setTechnicianOptions] = useState<
    TechnicianListItem[]
  >([]);
  const [technicianSearch, setTechnicianSearch] = useState('');
  const [technicianPage, setTechnicianPage] = useState(1);
  const [technicianTotal, setTechnicianTotal] = useState(0);
  const [technicianOptionsLoading, setTechnicianOptionsLoading] =
    useState(true);
  const [technicianOptionsLoadingMore, setTechnicianOptionsLoadingMore] =
    useState(false);

  const debouncedTechnicianSearch = useDebounce(technicianSearch, 500);

  // ── URL → state  (browser back / forward) ────────────────────────────────
  useEffect(() => {
    isSyncingFromUrl.current = true;

    const urlPage = searchParams.get('page');
    const urlLimit = searchParams.get('limit');
    const urlSearch = (searchParams.get('search') ?? '').trimStart();
    const urlSortOrder = searchParams.get('sortOrder');
    const urlEquipmentIds = idsFromParam(searchParams.get('equipment'));
    const urlTechnicianIds = idsFromParam(searchParams.get('technician'));
    const urlStartDate = searchParams.get('startDate') ?? '';
    const urlEndDate = searchParams.get('endDate') ?? '';

    const nextPage = (() => {
      const v = urlPage ? parseInt(urlPage, 10) : 1;
      return !isNaN(v) && v >= 1 ? v : 1;
    })();
    if (nextPage !== page) {
      setPage(nextPage);
    }

    const nextLimit = (() => {
      const v = urlLimit ? parseInt(urlLimit, 10) : DEFAULT_LIMIT;
      return isAllowedLimit(v) ? v : DEFAULT_LIMIT;
    })();
    if (nextLimit !== limit) {
      setLimit(nextLimit);
    }

    const nextSearch =
      urlSearch.length > MAX_SEARCH_LENGTH
        ? urlSearch.slice(0, MAX_SEARCH_LENGTH)
        : urlSearch;
    if (nextSearch !== search) {
      setSearchState(nextSearch);
    }

    const nextSortOrder: 'asc' | 'desc' =
      urlSortOrder && isValidSortOrder(urlSortOrder) ? urlSortOrder : 'desc';
    if (nextSortOrder !== sortOrder) {
      setSortOrder(nextSortOrder);
    }

    const nextEquipmentStr = urlEquipmentIds.join(',');
    if (nextEquipmentStr !== equipmentIds.join(',')) {
      setEquipmentIds(urlEquipmentIds);
      setAppliedFilters((prev) => ({ ...prev, equipmentIds: urlEquipmentIds }));
    }

    const nextTechnicianStr = urlTechnicianIds.join(',');
    if (nextTechnicianStr !== technicianIds.join(',')) {
      setTechnicianIds(urlTechnicianIds);
      setAppliedFilters((prev) => ({
        ...prev,
        technicianIds: urlTechnicianIds,
      }));
    }

    const nextDateRange = { startDate: urlStartDate, endDate: urlEndDate };
    setDateRange((prev) => {
      if (
        prev.startDate === nextDateRange.startDate &&
        prev.endDate === nextDateRange.endDate
      ) {
        return prev;
      }
      return nextDateRange;
    });
    setAppliedFilters((prev) => {
      if (
        prev.dateRange.startDate === urlStartDate &&
        prev.dateRange.endDate === urlEndDate
      ) {
        return prev;
      }
      return { ...prev, dateRange: nextDateRange };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // Reset isSyncingFromUrl after state settles
  useEffect(() => {
    if (isSyncingFromUrl.current) {
      isSyncingFromUrl.current = false;
    }
  }, [page, limit, search, sortOrder, appliedFilters]);

  // ── state → URL ───────────────────────────────────────────────────────────
  useEffect(() => {
    if (isSyncingFromUrl.current) {
      return;
    }

    const params = new URLSearchParams();
    params.set('page', page.toString());
    params.set('limit', limit.toString());

    const trimmedSearch = search.trim();
    if (trimmedSearch) {
      params.set('search', trimmedSearch);
    }

    if (sortOrder !== 'desc') {
      params.set('sortOrder', sortOrder);
    }

    if (appliedFilters.equipmentIds.length > 0) {
      params.set('equipment', appliedFilters.equipmentIds.join(','));
    }

    if (appliedFilters.technicianIds.length > 0) {
      params.set('technician', appliedFilters.technicianIds.join(','));
    }

    if (appliedFilters.dateRange.startDate) {
      params.set('startDate', appliedFilters.dateRange.startDate);
    }
    if (appliedFilters.dateRange.endDate) {
      params.set('endDate', appliedFilters.dateRange.endDate);
    }

    // Preserve params owned by parent components (ViewClient uses 'tab').
    const tab = searchParams.get('tab');
    if (tab) {
      params.set('tab', tab);
    }

    if (params.toString() !== searchParams.toString()) {
      setSearchParams(params, { replace: false });
    }
  }, [
    page,
    limit,
    search,
    sortOrder,
    appliedFilters,
    searchParams,
    setSearchParams,
  ]);

  // ── Commit debounced search → applied filter, reset to page 1 ─────────────
  useEffect(() => {
    if (prevDebouncedRef.current === debouncedSearch) {
      return;
    }
    prevDebouncedRef.current = debouncedSearch;
    setAppliedFilters((prev) => ({ ...prev, certificateId: debouncedSearch }));
    setPage(1);
  }, [debouncedSearch]);

  // ── Certificate list: main fetch ──────────────────────────────────────────
  useEffect(() => {
    setLoading(true);
    const controller = new AbortController();
    setFetchError(false);

    const run = async () => {
      try {
        const start = (appliedFilters.dateRange.startDate ?? '').trim();
        const end = (appliedFilters.dateRange.endDate ?? '').trim();
        const result = await listCertificates({
          page,
          limit,
          ...(appliedFilters.certificateId && {
            certificateId: appliedFilters.certificateId,
          }),
          ...(appliedFilters.equipmentIds.length > 0 && {
            equipmentIds: appliedFilters.equipmentIds,
          }),
          ...(appliedFilters.technicianIds.length > 0 && {
            technicianIds: appliedFilters.technicianIds,
          }),
          ...(start && { startDate: start }),
          ...(end && { endDate: end }),
          sortOrder,
        });

        if (controller.signal.aborted) {
          return;
        }

        const totalPages = Math.ceil(result.data.total / limit);
        if (result.data.total > 0 && totalPages > 0 && page > totalPages) {
          setPage(totalPages);
          return;
        }

        setItems(result.data.items);
        setTotalCount(result.data.total);
      } catch (err) {
        if (!controller.signal.aborted) {
          const e = err as { status?: number };
          if (e?.status === 403) {
            setAccessDenied(true);
          } else {
            setFetchError(true);
          }
          setItems([]);
          setTotalCount(0);
        }
      } finally {
        if (!controller.signal.aborted) {
          setLoading(false);
        }
      }
    };

    void run();
    return () => controller.abort();
  }, [page, limit, appliedFilters, sortOrder]);

  // ── Equipment dropdown: fetch on search or page change ────────────────────
  useEffect(() => {
    const isFirstPage = equipmentPage === 1;
    if (isFirstPage) {
      setEquipmentOptionsLoading(true);
    } else {
      setEquipmentOptionsLoadingMore(true);
    }

    let cancelled = false;

    const run = async () => {
      try {
        const result = await listCertificateEquipment({
          page: equipmentPage,
          limit: EQUIPMENT_LIMIT,
          ...(debouncedEquipmentSearch.trim() && {
            search: debouncedEquipmentSearch.trim(),
          }),
        });

        if (cancelled) {
          return;
        }
        setEquipmentOptions((prev) =>
          isFirstPage ? result.items : [...prev, ...result.items]
        );
        setEquipmentTotal(result.total);
      } catch {
        if (!cancelled && isFirstPage) {
          setEquipmentOptions([]);
        }
      } finally {
        if (!cancelled) {
          if (isFirstPage) {
            setEquipmentOptionsLoading(false);
          } else {
            setEquipmentOptionsLoadingMore(false);
          }
        }
      }
    };

    void run();
    return () => {
      cancelled = true;
    };
  }, [equipmentPage, debouncedEquipmentSearch]);

  useEffect(() => {
    setEquipmentPage(1);
    setEquipmentOptions([]);
  }, [debouncedEquipmentSearch]);

  // ── Technician dropdown: fetch on search or page change ───────────────────
  useEffect(() => {
    const isFirstPage = technicianPage === 1;
    if (isFirstPage) {
      setTechnicianOptionsLoading(true);
    } else {
      setTechnicianOptionsLoadingMore(true);
    }

    let cancelled = false;

    const run = async () => {
      try {
        const result = await listTechnicianApiService.listTechnicians({
          ...(debouncedTechnicianSearch.trim() && {
            search: debouncedTechnicianSearch.trim(),
          }),
          page: technicianPage,
          limit: TECHNICIAN_LIMIT,
        });

        if (cancelled) {
          return;
        }
        setTechnicianOptions((prev) =>
          isFirstPage ? result.data : [...prev, ...result.data]
        );
        setTechnicianTotal(result.totalCount);
      } catch {
        if (!cancelled && isFirstPage) {
          setTechnicianOptions([]);
        }
      } finally {
        if (!cancelled) {
          if (isFirstPage) {
            setTechnicianOptionsLoading(false);
          } else {
            setTechnicianOptionsLoadingMore(false);
          }
        }
      }
    };

    void run();
    return () => {
      cancelled = true;
    };
  }, [technicianPage, debouncedTechnicianSearch]);

  useEffect(() => {
    setTechnicianPage(1);
    setTechnicianOptions([]);
  }, [debouncedTechnicianSearch]);

  const equipmentHasMore = equipmentOptions.length < equipmentTotal;
  const technicianHasMore = technicianOptions.length < technicianTotal;

  const loadMoreEquipment = useCallback(() => {
    if (
      !equipmentOptionsLoading &&
      !equipmentOptionsLoadingMore &&
      equipmentHasMore
    ) {
      setEquipmentPage((prev) => prev + 1);
    }
  }, [equipmentOptionsLoading, equipmentOptionsLoadingMore, equipmentHasMore]);

  const loadMoreTechnicians = useCallback(() => {
    if (
      !technicianOptionsLoading &&
      !technicianOptionsLoadingMore &&
      technicianHasMore
    ) {
      setTechnicianPage((prev) => prev + 1);
    }
  }, [
    technicianOptionsLoading,
    technicianOptionsLoadingMore,
    technicianHasMore,
  ]);

  const handleEquipmentSearchChange = useCallback((q: string) => {
    setEquipmentSearch(q);
  }, []);

  const handleTechnicianSearchChange = useCallback((q: string) => {
    setTechnicianSearch(q);
  }, []);

  const setSearch = useCallback(
    (next: string) => {
      const normalized = next.trimStart();
      if (normalized.length > MAX_SEARCH_LENGTH) {
        return;
      }
      if (!normalized.trim() && search.trim()) {
        setLoading(true);
      }
      setSearchState(normalized);
    },
    [search]
  );

  const handleSort = useCallback((columnKey: string) => {
    if (columnKey !== 'generated_at') {
      return;
    }
    setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    setPage(1);
  }, []);

  const handleLimitChange = useCallback((newLimit: number) => {
    if (!isAllowedLimit(newLimit)) {
      setLimitError('Dozwolone wartości: 10, 30, 50, 100.');
      return;
    }
    setLimitError('');
    setLimit(newLimit);
    setPage(1);
  }, []);

  const handleApplyFilters = useCallback(() => {
    setAppliedFilters({
      certificateId: search,
      equipmentIds,
      technicianIds,
      dateRange,
    });
    prevDebouncedRef.current = search;
    setPage(1);
  }, [search, equipmentIds, technicianIds, dateRange]);

  const handleClearFilters = useCallback(() => {
    setEquipmentIds([]);
    setTechnicianIds([]);
    setDateRange(emptyDateRange);
    setAppliedFilters((prev) => ({
      ...prev,
      equipmentIds: [],
      technicianIds: [],
      dateRange: emptyDateRange,
    }));
    setPage(1);
  }, []);

  const isNoFilterFetch = useMemo(
    () =>
      !appliedFilters.certificateId &&
      appliedFilters.equipmentIds.length === 0 &&
      appliedFilters.technicianIds.length === 0 &&
      !hasDateFilter(appliedFilters.dateRange),
    [appliedFilters]
  );

  const isSearchClearing =
    !search.trim() &&
    debouncedSearch.trim() !== '' &&
    appliedFilters.equipmentIds.length === 0 &&
    appliedFilters.technicianIds.length === 0 &&
    !hasDateFilter(appliedFilters.dateRange);

  return {
    items,
    loading,
    page,
    setPage,
    limit,
    totalCount,
    isNoFilterFetch,
    fetchError,
    accessDenied,
    search,
    isSearchClearing,
    setSearch,
    equipmentIds,
    setEquipmentIds,
    technicianIds,
    setTechnicianIds,
    dateRange,
    setDateRange,
    appliedFilters,
    handleApplyFilters,
    handleClearFilters,
    limitError,
    handleLimitChange,
    sortColumn: 'generated_at',
    sortOrder,
    handleSort,
    equipmentOptions,
    equipmentOptionsLoading,
    equipmentOptionsLoadingMore,
    loadMoreEquipment,
    handleEquipmentSearchChange,
    technicianOptions,
    technicianOptionsLoading,
    technicianOptionsLoadingMore,
    loadMoreTechnicians,
    handleTechnicianSearchChange,
  };
}
